import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Typography, Grid, Box, Avatar, Button, Pagination, Skeleton, Tabs, Tab, Chip, useMediaQuery, Theme, Link, Tooltip, IconButton, Snackbar } from '@mui/material';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import LinkIcon from '@mui/icons-material/Link';
import { getUserProfile, getGamesByStatus, updateUserProfile, getGamesByStatusForUser, getPublicGameStatuses, getProfileGameStatuses, getPlatforms, PlatformFilterType } from '../services/api';
import GameCard from '../components/GameCard';
import { UserProfile } from '../types/UserProfile';
import { Game, Platform } from '../types/Game';
import { AxiosResponse } from 'axios';
import LoadingPlaceholder from '../components/LoadingPlaceholder';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import GameListFilter from '../components/filter/GameListFilter';
import { Helmet } from 'react-helmet-async';
import { SITE_NAME } from '../constants/site';
import OnlineStatus from '../components/OnlineStatus';
import GameListComponent from '../components/GameList/GameListComponent';
import CreateGameList from '../components/GameList/CreateGameList';
import { getUserGameLists, GameListSummary, GameList, deleteGameList } from '../services/contents_api';
import EditGameList from '../components/GameList/EditGameList';
import ShareButtons from '../components/common/ShareButtons';
import { isAuthenticated } from '../services/auth';
import ActivityTab from '../components/profile/Activity';
import ProfileTab from '../components/ProfileTab';
import SettingsIcon from '@mui/icons-material/Settings';
import GetAppIcon from '@mui/icons-material/GetApp';
import Expandable from '../components/common/Expandable';
import { isPwa } from '../utils/isPwa';
import ReviewsTab from '../components/ReviewsTab';
import MemoriesTab from '../components/MemoriesTab';
import FollowButton from '../components/common/FollowButton';
import FollowsTab from '../components/FollowsTab';
import TimelineTab from '../components/TimelineTab';
import BoltIcon from '@mui/icons-material/Bolt';
import PlayLogsTab from '../components/PlayLogsTab';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TuneIcon from '@mui/icons-material/Tune';
import GotyTab from '../components/GotyTab';
import { isDev } from '../utils/env';
import FeatureAnnouncementDialog from '../components/dialogs/FeatureAnnouncementDialog';
import SocialLinks from '../components/profile/SocialLinks';
import { getLatestPopupReleaseNote } from '../services/api';
import { ReleaseNote } from '../types/ReleaseNote';
import { getSteamPlayerSummary, SteamPlayerSummary, getGameIdBySteamId } from '../services/SteamApi';

const GAMES_PER_PAGE = 30;

interface ProfilePageProps {
  profile?: UserProfile;
  isPublic?: boolean;
  isOwnProfile?: boolean;
}

// タブの種類を定数で定義
const MAIN_TABS = {
  PROFILE: 'profile',
  LIBRARY: 'library',
  LISTS: 'lists',
  REVIEWS: 'reviews',
  MEMORIES: 'memories',
  ACTIVITY: 'activity',
  FOLLOWS: 'follows',
  TIMELINE: 'timeline',
  PLAYLOGS: 'playlogs',
  GOTY: 'goty',
} as const;

type MainTabType = typeof MAIN_TABS[keyof typeof MAIN_TABS];

const ProfilePage: React.FC<ProfilePageProps> = ({ 
  profile: initialProfile, 
  isPublic = false,
  isOwnProfile = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { username } = useParams<{ username: string }>();
  const queryParams = new URLSearchParams(location.search);
  
  // 初期値の設定を修正
  const tab = queryParams.get('tab') as MainTabType;
  const status = parseInt(queryParams.get('status') || '1', 10);
  
  // 初期値を設定する際にタブの状態も考慮
  const [tabValue, setTabValue] = useState(tab === MAIN_TABS.LIBRARY ? status : 1);
  const [mainTabValue, setMainTabValue] = useState<MainTabType>(
    tab || (isPublic ? MAIN_TABS.PROFILE : MAIN_TABS.LIBRARY)
  );
  const initialPage = parseInt(queryParams.get('page') || '1', 10);
  const initialSearchQuery = queryParams.get('query') || '';
  const initialOrder = queryParams.get('order') || 'default';
  const initialPlatforms = queryParams.get('platforms') ? queryParams.get('platforms')!.split(',').map(Number) : [];
  const initialFilterType = (queryParams.get('filter_type') as PlatformFilterType) || 'user_platform';

  const [profile, setProfile] = useState<UserProfile | null>(initialProfile || null);
  const [isEditing, setIsEditing] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [page, setPage] = useState(initialPage);
  const [listTotalPages, setListTotalPages] = useState(0);
  const [gameCounts, setGameCounts] = useState<number[]>([0, 0, 0, 0, 0]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [order, setOrder] = useState(initialOrder);
  const [gameLists, setGameLists] = useState<GameListSummary[]>([]);
  const [createListDialogOpen, setCreateListDialogOpen] = useState(false);
  const [editingList, setEditingList] = useState<GameList | null>(null);
  const initialListPage = parseInt(queryParams.get('list_page') || '1', 10);
  const [listPage, setListPage] = useState(initialListPage);
  const [priorityMode, setPriorityMode] = useState(false);
  const [latestReleaseNote, setLatestReleaseNote] = useState<ReleaseNote | null>(null);
  const [steamStatus, setSteamStatus] = useState<SteamPlayerSummary | null>(null);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<number[]>(initialPlatforms);
  const [platformFilterType, setPlatformFilterType] = useState<PlatformFilterType>(initialFilterType);

  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const scrollPositionRef = useRef<number>(0);

  // スクロール位置を保存
  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // タブ変更時のスクロール位置を管理
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const currentScroll = scrollPositionRef.current;
    setTabValue(newValue);
    setPage(1);
    // URLを更新
    updateUrl(newValue, 1, searchQuery, order, selectedPlatforms, platformFilterType);
    // スクロール位置を復元
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScroll);
    });
    // クリックされたタブを中央にスクロール
    const target = event.currentTarget as HTMLElement;
    if (target) {
      setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }, 0);
    }
  };

  // 認証チェックを追加
  useEffect(() => {
    const checkAuth = async () => {
      if (!isPublic) {
        const authenticated = await isAuthenticated();
        if (!authenticated) {
          navigate('/login', { state: { from: location.pathname } });
        }
      }
    };

    checkAuth();
  }, [isPublic, navigate, location.pathname]);

  // リリースノートを取得（非公開プロフィールのみ）
  useEffect(() => {
    const fetchLatestReleaseNote = async () => {
      if (!isPublic) {
        try {
          const data = await getLatestPopupReleaseNote();
          setLatestReleaseNote(data);
        } catch (error) {
          console.error('リリースノートの取得に失敗しました:', error);
        }
      }
    };

    fetchLatestReleaseNote();
  }, [isPublic]);

  const fetchProfile = useCallback(async () => {
    if (isPublic || initialProfile) return;
    try {
      const response: AxiosResponse<UserProfile> = await getUserProfile();
      setProfile(response.data);
      setGameCounts(response.data.status_counts || [0, 0, 0, 0, 0]);
    } catch (error) {
      console.error('プロフィールの取得に失敗しました', error);
    } finally {
      setLoadingProfile(false);
    }
  }, [isPublic, initialProfile]);

  useEffect(() => {
    if (isPublic && initialProfile) {
      setProfile(initialProfile);
      setGameCounts(initialProfile.status_counts || [0, 0, 0, 0, 0]);
      setLoadingProfile(false);
    } else if (!isPublic) {
      fetchProfile();
    }
  }, [isPublic, initialProfile, fetchProfile]);

  // Steamステータスを取得（steam_idがある場合）
  useEffect(() => {
    const fetchSteamStatus = async () => {
      if (profile?.steam_id) {
        const status = await getSteamPlayerSummary(profile.steam_id);
        setSteamStatus(status);
      }
    };
    fetchSteamStatus();
  }, [profile?.steam_id]);

  // プラットフォーム一覧を取得
  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        const data = await getPlatforms();
        setPlatforms(data);
      } catch (error) {
        console.error('プラットフォームの取得に失敗しました:', error);
      }
    };
    fetchPlatforms();
  }, []);

  // Steamプレイ中ゲームのクリックハンドラー
  const handleSteamGameClick = async () => {
    if (!steamStatus?.gameid) return;

    // PULSEのgame_idを取得
    const pulseGameId = await getGameIdBySteamId(steamStatus.gameid);
    if (pulseGameId) {
      navigate(`/game/${pulseGameId}`);
    } else {
      setSnackbarMessage('データベースに登録されていないか、PULSEとSteamの連携が完了していないゲームです');
      setOpenSnackbar(true);
    }
  };

  // フィルターがアクティブかどうかを判定（現状はプラットフォームフィルターのみ）
  const hasActiveFilter = useMemo(() => {
    return selectedPlatforms.length > 0 || platformFilterType !== 'user_platform';
  }, [selectedPlatforms, platformFilterType]);

  // React Queryでゲーム一覧を取得
  // 公開プロフィールはURLのusernameを使用（安定したキー）、マイページは'me'を使用
  const stableUsername = isPublic ? username : 'me';
  const {
    data: gamesData,
    isLoading,
  } = useQuery({
    queryKey: ['profileGames', stableUsername, tabValue, page, searchQuery, order, selectedPlatforms, platformFilterType],
    queryFn: async () => {
      if (isPublic) {
        // isPublicの場合、この時点でprofile?.usernameは必ず存在（enabledで保証）
        return getGamesByStatusForUser(profile!.username, tabValue, page, searchQuery, order, selectedPlatforms, platformFilterType);
      } else {
        return getGamesByStatus(tabValue, page, searchQuery, order, selectedPlatforms, platformFilterType);
      }
    },
    enabled: !loadingProfile && mainTabValue === MAIN_TABS.LIBRARY && (!isPublic || !!profile?.username),
    staleTime: isPublic
      ? 30000 // 公開プロフィール：30秒（Redisキャッシュもあり、自分で編集しないので長め）
      : (hasActiveFilter ? 4000 : 2000), // マイページ：フィルター適用時4秒、通常2秒
  });

  const games: Game[] = gamesData?.data.results || [];
  const totalGames = gamesData?.data.count || 0;
  const gamesTotalPages = Math.ceil(totalGames / GAMES_PER_PAGE);
  // キャッシュがある場合はローディング表示をスキップ（スクロール位置保持のため）
  const loadingGames = isLoading && !gamesData;

  // ステータス取得用のgameIds（メモ化してqueryKeyの安定性を確保）
  const gameIds = useMemo(() => games.map(game => game.game_id), [games]);

  // React Queryでゲームステータスを取得（一覧とセットでキャッシュ）
  const { data: gameStatusesData } = useQuery({
    queryKey: ['profileGameStatuses', stableUsername, gameIds],
    queryFn: async () => {
      if (isPublic && profile?.username) {
        return getPublicGameStatuses(profile.username, gameIds);
      } else {
        return getProfileGameStatuses(gameIds);
      }
    },
    enabled: gameIds.length > 0 && (!isPublic || !!profile?.username),
    staleTime: isPublic
      ? 30000 // 公開プロフィール：30秒
      : (hasActiveFilter ? 4000 : 2000), // マイページ：フィルター適用時4秒、通常2秒
  });

  const gameStatuses: {
    [key: number]: {
      status: number | null;
      score: number | null;
      review_text: string | null;
      play_start_date: string | null;
      play_end_date: string | null;
      play_time: number | null;
      sub_status: number[] | null;
      score_tags: string[] | null;
      platform: number[] | null;
      priority?: number;
    }
  } = gameStatusesData || {};

  const handleEditClick = () => {
    navigate('/profile/edit');
  };

  const handleSave = async (updatedProfile: UserProfile) => {
    try {
      const response = await updateUserProfile(updatedProfile);
      setIsEditing(false);
      setProfile(response.data);
    } catch (error: any) {
      if (error.response && error.response.data) {
        console.error('プロフィールの更新に失敗しました', error.response.data);
        throw error; // エラーを再スローして、ProfileEditFormで処理できるようにする
      }
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    // URLを更新
    updateUrl(tabValue, value, searchQuery, order, selectedPlatforms, platformFilterType);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(1);
    // URLを更新
    updateUrl(tabValue, 1, query, order, selectedPlatforms, platformFilterType);
  };

  const handleOrderChange = (newOrder: string) => {
    setOrder(newOrder);
    setPage(1);
    // URLを更新
    updateUrl(tabValue, 1, searchQuery, newOrder, selectedPlatforms, platformFilterType);
  };

  const handlePlatformChange = (platformIds: number[]) => {
    setSelectedPlatforms(platformIds);
    setPage(1);
    // URLを更新
    updateUrl(tabValue, 1, searchQuery, order, platformIds, platformFilterType);
  };

  const handlePlatformFilterTypeChange = (filterType: PlatformFilterType) => {
    setPlatformFilterType(filterType);
    setPage(1);
    // URLを更新
    updateUrl(tabValue, 1, searchQuery, order, selectedPlatforms, filterType);
  };

  // プラットフォームとフィルタータイプを同時に更新するハンドラ
  const handleFilterApply = (platformIds: number[], filterType: PlatformFilterType) => {
    setSelectedPlatforms(platformIds);
    setPlatformFilterType(filterType);
    setPage(1);
    updateUrl(tabValue, 1, searchQuery, order, platformIds, filterType);
  };

  // URLを更新する関数
  const updateUrl = (status: number, page: number, query: string, order: string, platformIds: number[] = [], filterType: PlatformFilterType = 'user_platform') => {
    const params = new URLSearchParams({
      status: status.toString(),
      page: page.toString(),
      query,
      order,
      tab: mainTabValue
    });
    if (platformIds.length > 0) {
      params.set('platforms', platformIds.join(','));
    }
    if (filterType !== 'user_platform') {
      params.set('filter_type', filterType);
    }

    const newUrl = isPublic
      ? `/profile/${username}?${params.toString()}`
      : `/profile?${params.toString()}`;
    navigate(newUrl, { replace: true });
  };

  const TabLabel: React.FC<{ label: string; count: number; isSelected: boolean }> = ({ label, count, isSelected }) => (
    <Box sx={{ display: 'flex', flexDirection: isSmallScreen ? 'column' : 'row', alignItems: 'center' }}>
      <Typography sx={{ mr: isSmallScreen ? 0 : 1, fontSize: isSmallScreen ? '0.75rem' : '0.8rem' }}>{label}</Typography>
      {isSelected ? (
        <Chip
          label={count}
          size="small"
          sx={{
            backgroundColor: 'primary.main',
            color: 'black',
            fontSize: isSmallScreen ? '0.625rem' : '0.75rem',
            height: isSmallScreen ? '16px' : '18px',
          }}
        />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: isSmallScreen ? '0.625rem' : '0.75rem' }}>
          /{count}
        </Typography>
      )}
    </Box>
  );

  const handleCopyLink = () => {
    const publicProfileUrl = `${window.location.origin}/profile/${profile?.username}`;
    navigator.clipboard.writeText(publicProfileUrl);
    setSnackbarMessage('リンクがコピーされました');
    setOpenSnackbar(true);
  };

  // 新しい関数を追加
  const handleClearFilter = () => {
    setSearchQuery('');
    setOrder('default');
    setSelectedPlatforms([]);
    setPlatformFilterType('user_platform');
    setPage(1);
    // URLを更新
    updateUrl(tabValue, 1, '', 'default', [], 'user_platform');
  };

  // メインタブ変更ハンドラー
  const handleMainTabChange = (event: React.SyntheticEvent, newValue: MainTabType) => {
    const currentScroll = scrollPositionRef.current;
    setMainTabValue(newValue);
    setPage(1);

    // 現在のパラメータを取得
    const params = new URLSearchParams(queryParams);

    // タブに応じて必要なパラメータのみを保持
    params.set('tab', newValue);
    params.set('page', '1');

    if (newValue === MAIN_TABS.LIBRARY) {
      // ライブラリタブの場合
      params.set('status', tabValue.toString());
      if (searchQuery) params.set('query', searchQuery);
      if (order !== 'default') params.set('order', order);
    } else if (newValue === MAIN_TABS.LISTS) {
      // リストタブの場合はリストページを保持
      params.set('list_page', listPage.toString());
    } else {
      // その他のタブではページ関連パラメータをクリア
      params.delete('status');
      params.delete('query');
      params.delete('order');
      params.delete('list_page');
    }

    const newUrl = isPublic
      ? `/profile/${username}?${params.toString()}`
      : `/profile?${params.toString()}`;
    navigate(newUrl, { replace: true });

    // スクロール位置を復元
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScroll);
    });

    // クリックされたタブを中央にスクロール
    const target = event.currentTarget as HTMLElement;
    if (target) {
      setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }, 0);
    }
  };

  // ゲームリストを取得する関数
  const fetchGameLists = useCallback(async () => {
    try {
      const response = await getUserGameLists(isPublic ? username : undefined, listPage);
      setGameLists(response.results);
      setListTotalPages(Math.ceil(response.count / 9));
    } catch (error) {
      console.error('ゲームリストの取得に失敗しました:', error);
    }
  }, [isPublic, username, listPage]);

  // コンポーネントのマウント時にゲームリストを取得
  useEffect(() => {
    if (mainTabValue === MAIN_TABS.LISTS) {
      fetchGameLists();
    }
  }, [mainTabValue, fetchGameLists]);

  // リストの編集ハンドラー
  const handleEditList = (list: GameListSummary) => {
    setEditingList(list as unknown as GameList); // 一時的な型キャスト
  };

  // リストの削除ハンドラー
  const handleDeleteList = async (listId: number) => {
    if (window.confirm('このリストを削除してもよろしいですか？')) {
      try {
        await deleteGameList(listId);
        fetchGameLists();
      } catch (error) {
        console.error('リストの削除に失敗しました:', error);
      }
    }
  };

  // URLのパラメータが変更されたときの処理を修正
  useEffect(() => {
    const newTab = queryParams.get('tab') as MainTabType;
    const newStatus = parseInt(queryParams.get('status') || '1', 10);
    const newListPage = parseInt(queryParams.get('list_page') || '1', 10);

    // メインタブが変更された場合
    if (newTab !== mainTabValue) {
      setMainTabValue(newTab || (isPublic ? MAIN_TABS.PROFILE : MAIN_TABS.LIBRARY));
    }

    // ライブラリタブでステータスが変更された場合
    if (newTab === MAIN_TABS.LIBRARY && newStatus !== tabValue) {
      setTabValue(newStatus);
      setPage(1);
    }
    
    // リストタブでページが変更された場合
    if (newTab === MAIN_TABS.LISTS && newListPage !== listPage) {
      setListPage(newListPage);
    }
  }, [location.search]); // location.searchの変更時のみ実行

  // リストページ変更ハンドラーを修正
  const handleListPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setListPage(value);
    // URLを更新
    const params = new URLSearchParams(queryParams);
    params.set('list_page', value.toString());
    params.set('tab', MAIN_TABS.LISTS);
    
    const newUrl = isPublic
      ? `/profile/${username}?${params.toString()}`
      : `/profile?${params.toString()}`;
    navigate(newUrl, { replace: true });
    
    // ページトップにスクロール
    window.scrollTo(0, 0);
  };

  return (
    <>
      <Helmet>
        <title>
          {isPublic && profile 
            ? `${profile.display_name || profile.username}(@${profile.username})のゲームライブラリ | ${SITE_NAME}`
            : `マイページ | ${SITE_NAME}`}
        </title>
      </Helmet>
      
      <Grid container spacing={3} sx={{ mb: { xs: 2, md: 2 } }}>
        <Grid item xs={12} md={8}>
          {loadingProfile ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Skeleton variant="circular" width={56} height={56} />
              <Box sx={{ ml: 2, flexGrow: 1 }}>
                <Skeleton width="40%" height={32} />
                <Skeleton width="60%" />
                <Skeleton width="80%" />
              </Box>
            </Box>
          ) : (
            profile && (
              <>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  mb: 1
                }}>
                  <Avatar
                    src={profile.avatar || undefined}
                    sx={{ width: 56, height: 56 }}
                  />
                  <Box sx={{ ml: 2 }}>
                    <Typography variant="h6" component="h1">{profile.display_name || profile.username}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        @{profile.username}
                      </Typography>
                      {isPublic && profile.last_access && !profile.current_playing_game_id && !steamStatus?.gameextrainfo && (
                        <OnlineStatus lastAccess={profile.last_access} />
                      )}
                      {/* Steamでプレイ中のゲーム表示 */}
                      {steamStatus?.gameextrainfo && !profile.current_playing_game_id && (
                        <Box
                          onClick={handleSteamGameClick}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            backgroundColor: 'rgba(102, 192, 244, 0.12)',
                            border: '1px solid rgba(102, 192, 244, 0.5)',
                            borderRadius: '4px',
                            padding: '1px 8px',
                            paddingLeft: '4px',
                            textDecoration: 'none',
                            position: 'relative',
                            overflow: 'hidden',
                            cursor: steamStatus.gameid ? 'pointer' : 'default',
                            '&:hover': {
                              opacity: steamStatus.gameid ? 0.8 : 1
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(90deg, rgba(102, 192, 244, 0), rgba(102, 192, 244, 0.2), rgba(102, 192, 244, 0))',
                              backgroundSize: '200% 100%',
                              animation: 'shimmer 2s infinite',
                            },
                            '@keyframes shimmer': {
                              '0%': {
                                backgroundPosition: '100% 0'
                              },
                              '100%': {
                                backgroundPosition: '-100% 0'
                              }
                            }
                          }}
                        >
                          <Box
                            sx={{
                              width: '0.85rem',
                              height: '0.85rem',
                              maskImage: 'url(/icons/service/steam_black.svg)',
                              maskSize: 'contain',
                              maskRepeat: 'no-repeat',
                              maskPosition: 'center',
                              WebkitMaskImage: 'url(/icons/service/steam_black.svg)',
                              WebkitMaskSize: 'contain',
                              WebkitMaskRepeat: 'no-repeat',
                              WebkitMaskPosition: 'center',
                              backgroundColor: '#e6e6e6',
                              zIndex: 1,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#e6e6e6',
                              fontSize: '0.7rem',
                              fontWeight: 400,
                              lineHeight: '1',
                              letterSpacing: '0.02em',
                              maxWidth: '180px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              zIndex: 1,
                            }}
                          >
                            {steamStatus.gameextrainfo}
                          </Typography>
                        </Box>
                      )}
                      {profile.current_playing_game_id && (
                        <Box
                          onClick={() => navigate(`/game/${profile.current_playing_game_id}`)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            backgroundColor: 'rgba(217, 255, 0, 0.12)',
                            border: '1px solid rgba(217, 255, 0, 0.5)',
                            borderRadius: '4px',
                            padding: '1px 8px',
                            paddingLeft: '4px',
                            position: 'relative',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(90deg, rgba(217, 255, 0, 0), rgba(217, 255, 0, 0.2), rgba(217, 255, 0, 0))',
                              backgroundSize: '200% 100%',
                              animation: 'shimmer 2s infinite',
                            },
                            '@keyframes shimmer': {
                              '0%': {
                                backgroundPosition: '100% 0'
                              },
                              '100%': {
                                backgroundPosition: '-100% 0'
                              }
                            }
                          }}
                        >
                          <PlayArrowIcon 
                            sx={{ 
                              fontSize: '0.875rem',
                              color: '#d9ff00',
                            }} 
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#d9ff00',
                              fontSize: '0.7rem',
                              fontWeight: 400,
                              lineHeight: '1',
                              letterSpacing: '0.02em',
                              zIndex: 1
                            }}
                          >
                            now playing
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
                {profile.bio && (
                  <Expandable maxLines={3} expandButtonStyle="icon">
                    <Typography variant="body2" sx={{ mb: 0.5, lineHeight: 1.25, whiteSpace: 'pre-wrap' }}>
                      {profile.bio}
                    </Typography>
                  </Expandable>
                )}
                {isPublic && profile.location && (
                  <Box sx={{
                    mb: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'text.secondary',
                  }}>
                    <FmdGoodOutlinedIcon sx={{ fontSize: '1rem' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.75rem'}}>{profile.location}</Typography>
                  </Box>
                )}
                {profile.social_links && Object.keys(profile.social_links).length > 0 && (
                  <SocialLinks
                    socialLinks={profile.social_links}
                    onCopy={(message) => {
                      setSnackbarMessage(message);
                      setOpenSnackbar(true);
                    }}
                  />
                )}
                {!isPublic && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                      <Link
                        href={`/profile/${profile.username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Chip
                          icon={<LinkIcon sx={{ fontSize: '0.875rem !important' }} />}
                          label={`公開URL: /profile/${profile.username}`}
                          size="small"
                          sx={{
                            height: '24px',
                            fontSize: '0.75rem',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            color: 'text.secondary',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.08)',
                              borderColor: 'primary.main',
                            },
                            '& .MuiChip-icon': {
                              marginLeft: '6px',
                              color: 'text.secondary'
                            }
                          }}
                        />
                      </Link>
                      <ShareButtons
                        text={`${profile.display_name || profile.username}のゲームライブラリ | ${SITE_NAME} - ゲーム記録管理プラットフォーム`}
                        url={`/profile/${profile.username}`}
                        hashtags={['PULSE_DB']}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SettingsIcon />}
                        onClick={() => navigate('/profile/settings')}
                        sx={{
                          color: 'text.primary',
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: '50px',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            borderColor: 'primary.main',
                          }
                        }}
                      >
                        プロフィール設定
                      </Button>
                      {!isPwa() && (
                        <Tooltip title="PWAアプリをインストール">
                          <IconButton
                            size="small"
                            onClick={() => navigate('/install-pwa')}
                            sx={{ 
                              border: '1px solid',
                              borderColor: 'divider',
                              backgroundColor: 'rgba(255, 255, 255, 0.04)',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                                borderColor: 'primary.main',
                              }
                            }}
                          >
                            <GetAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0 }}>
                  <Box>
                    {isPublic && (
                      <FollowButton
                        username={profile.username}
                        isFollowing={profile.is_following}
                        isOwnProfile={isOwnProfile}
                        isFollowedBy={profile.is_followed_by}
                        onFollowChange={(isFollowing) => {
                          setProfile(prev => prev ? {
                            ...prev,
                            is_following: isFollowing,
                            followers_count: prev.followers_count + (isFollowing ? 1 : -1)
                          } : null);
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </>
            )
          )}
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
          {/* 右側のカラムに追加のコンテンツを配置できます */}
        </Grid>
      </Grid>

      {/* 追加：メインタブ */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs 
          value={mainTabValue} 
          onChange={handleMainTabChange}
          variant="scrollable"
          sx={{
            minHeight: { xs: '32px', sm: '40px' },
            '& .MuiTab-root': {
              minHeight: { xs: '36px', sm: '40px' },
              py: { xs: 1, sm: 1 },
              px: { xs: 1, sm: 1.5 },
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
              minWidth: 0
            }
          }}
        >
          {!isPublic && (
            <Tab 
              icon={
                <Box 
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    borderRadius: '4px',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.12)'
                    }
                  }}
                >
                  <BoltIcon 
                    sx={{ 
                      fontSize: '1rem',
                      transform: 'rotate(20deg)' // 20度反時計回りに回転
                    }} 
                  />
                </Box>
              }
              value={MAIN_TABS.TIMELINE}
            />
          )}
          <Tab label="プロフィール" value={MAIN_TABS.PROFILE} />
          <Tab label="ライブラリ" value={MAIN_TABS.LIBRARY} />
          <Tab label="レビュー" value={MAIN_TABS.REVIEWS} />
          <Tab label="メモリー" value={MAIN_TABS.MEMORIES} />
          <Tab label="プレイログ" value={MAIN_TABS.PLAYLOGS} />
          <Tab label="リスト" value={MAIN_TABS.LISTS} />
          <Tab label="GOTY" value={MAIN_TABS.GOTY} />
          <Tab label="アクティビティ" value={MAIN_TABS.ACTIVITY} />
          <Tab label="フォロー" value={MAIN_TABS.FOLLOWS} />
        </Tabs>
      </Box>

      {/* ゲームタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.LIBRARY && (
        <Box sx={{ width: '100%' }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2 
          }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              variant="scrollable"
              scrollButtons="auto"
              TabIndicatorProps={{ sx: { display: 'none' } }}
              sx={{ 
                maxWidth: '100%',
                '& .MuiTab-root': {
                  minWidth: isSmallScreen ? 'auto' : 'inherit',
                  padding: isSmallScreen ? '6px 8px' : '6px 16px',
                  minHeight: '40px',
                  borderRadius: '8px',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  }
                }
              }}
            >
              <Tab 
                label={<TabLabel label="プレイ済" count={gameCounts[0]} isSelected={tabValue === 1} />} 
                value={1} 
              />
              <Tab 
                label={<TabLabel label="プレイ中" count={gameCounts[1]} isSelected={tabValue === 2} />} 
                value={2} 
              />
              <Tab 
                label={<TabLabel label="中断" count={gameCounts[2]} isSelected={tabValue === 3} />} 
                value={3} 
              />
              <Tab 
                label={<TabLabel label="積みゲー" count={gameCounts[3]} isSelected={tabValue === 4} />} 
                value={4} 
              />
              <Tab 
                label={<TabLabel label={isSmallScreen ? "ウィッシュ" : "ウィッシュリスト"} count={gameCounts[4]} isSelected={tabValue === 5} />} 
                value={5} 
              />
            </Tabs>
          </Box>
          
          <Box sx={{ mt: 1 }}>
            <GameListFilter
              onSearch={handleSearch}
              onOrderChange={handleOrderChange}
              onClearFilter={handleClearFilter}
              totalGames={totalGames}
              initialSearchQuery={searchQuery}
              initialOrder={order}
              onPriorityModeChange={!isPublic ? setPriorityMode : undefined}
              priorityMode={priorityMode}
              currentStatus={tabValue}
              platforms={platforms}
              selectedPlatforms={selectedPlatforms}
              onPlatformChange={handlePlatformChange}
              platformFilterType={platformFilterType}
              onPlatformFilterTypeChange={handlePlatformFilterTypeChange}
              onFilterApply={handleFilterApply}
            />
          </Box>
          {loadingGames ? (
            <LoadingPlaceholder type="card" count={GAMES_PER_PAGE} />
          ) : (
            <>
              {games.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 8,
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: 2,
                  mt: 2
                }}>
                  <Typography variant="h6" gutterBottom>
                    まだゲームが登録されていません
                  </Typography>
                  {!isPublic && (
                    <>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        ゲームを検索して、ライブラリに追加しましょう
                      </Typography>
                      <Button 
                        variant="contained" 
                        onClick={() => navigate('/search')}
                      >
                        ゲームを探す
                      </Button>
                    </>
                  )}
                </Box>
              ) : (
                <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                  {games.map((game) => (
                    <Grid item xs={4} sm={3} md={2} key={game.game_id}>
                      <GameCard
                        game={game}
                        mode={isPublic ? 'public' : 'profile'}
                        username={isPublic ? profile?.username : undefined}
                        initialStatus={gameStatuses[game.game_id]?.status}
                        initialScore={gameStatuses[game.game_id]?.score}
                        initialReviewText={gameStatuses[game.game_id]?.review_text}
                        initialPlayStartDate={gameStatuses[game.game_id]?.play_start_date}
                        initialPlayEndDate={gameStatuses[game.game_id]?.play_end_date}
                        initialPlayTime={gameStatuses[game.game_id]?.play_time}
                        initialSubStatus={gameStatuses[game.game_id]?.sub_status}
                        initialScoreTags={gameStatuses[game.game_id]?.score_tags}
                        initialPlatform={gameStatuses[game.game_id]?.platform}
                        initialPriority={gameStatuses[game.game_id]?.priority} // TODO: バックエンドから取得するように修正
                        priorityMode={priorityMode}
                        userSteamId={!isPublic ? profile?.steam_id : undefined}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
              {gamesTotalPages > 1 && (
                <Pagination
                  count={gamesTotalPages}
                  page={page}
                  onChange={handlePageChange}
                  sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}
                />
              )}
            </>
          )}
        </Box>
      )}

      {/* コンテンツタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.LISTS && (
        <Box>
          {!isPublic && (
            <Button 
              variant="contained"
              size="small"
              onClick={() => setCreateListDialogOpen(true)}
              sx={{ 
                mb: 3,
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 400,
                letterSpacing: '0.02em',
                textTransform: 'none',
                borderRadius: '6px',
                px: 2,
                py: 0.5,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  transform: 'translateY(-1px)'
                },
                '&:active': {
                  transform: 'translateY(0)'
                }
              }}
            >
              新規リストを作成
            </Button>
          )}
          
          <Grid container spacing={2}>
            {gameLists.map((list) => (
              <GameListComponent
                key={list.id}
                list={list}
                onEdit={!isPublic ? handleEditList : undefined}
                onDelete={!isPublic ? handleDeleteList : undefined}
              />
            ))}
          </Grid>

          {listTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={listTotalPages}
                page={listPage}
                onChange={handleListPageChange}
              />
            </Box>
          )}

          <CreateGameList
            open={createListDialogOpen}
            onClose={() => setCreateListDialogOpen(false)}
            onListCreated={fetchGameLists}
          />

          {editingList && (
            <EditGameList
              open={!!editingList}
              onClose={() => setEditingList(null)}
              list={editingList}
              onListUpdated={fetchGameLists}
            />
          )}
        </Box>
      )}

      {/* GOTYタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.GOTY && profile && (
        <GotyTab
          username={profile.username}
          isPublic={isPublic}
        />
      )}

      {/* レビュータブのコンテンツ */}
      {mainTabValue === MAIN_TABS.REVIEWS && profile && (
        <ReviewsTab 
          username={profile.username} 
          isPublic={isPublic}
        />
      )}

      {/* メモリータブのコンテンツ */}
      {mainTabValue === MAIN_TABS.MEMORIES && profile && (
        <MemoriesTab 
          username={profile.username} 
          isPublic={isPublic}
          userProfile={profile}
        />
      )}

      {/* アクティビティタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.ACTIVITY && (
        <ActivityTab 
          username={profile?.username} 
          isPublic={isPublic} 
        />
      )}

      {/* プロフィールタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.PROFILE && (
        <ProfileTab 
          profile={profile!}
          isPublic={isPublic}
        />
      )}

      {/* フォロータブのコンテンツ */}
      {mainTabValue === MAIN_TABS.FOLLOWS && profile && (
        <FollowsTab 
          username={profile.username} 
          isPublic={isPublic} 
        />
      )}

      {/* タイムラインタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.TIMELINE && (
        <TimelineTab 
          username={profile?.username || ''} 
          isPublic={isPublic} 
        />
      )}

      {/* プレイログタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.PLAYLOGS && profile && (
        <PlayLogsTab
          username={profile.username}
          isPublic={isPublic}
        />
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={snackbarMessage}
      />

      {/* リリースノートのお知らせダイアログ */}
      {!isPublic && latestReleaseNote && (
        <FeatureAnnouncementDialog
          announcement={{
            id: `release-note-${latestReleaseNote.id}`,
            title: latestReleaseNote.title,
            description: latestReleaseNote.description,
            linkText: latestReleaseNote.link_text,
            linkUrl: latestReleaseNote.link_url,
            badge: 'NEW',
            badgeColor: 'primary',
            version: latestReleaseNote.version,
          }}
          storageKey="latest-release-note-dismissed"
          onNavigate={latestReleaseNote.link_url ? () => {
            if (latestReleaseNote.link_url!.startsWith('http')) {
              window.open(latestReleaseNote.link_url, '_blank', 'noopener noreferrer');
            } else {
              navigate(latestReleaseNote.link_url!);
            }
          } : undefined}
        />
      )}
    </>
  );
};

export default ProfilePage;
